import { useState } from "react";
import { useSearchParams } from "react-router";
import { Badge, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "../api";
import { useAdminResource } from "../hooks";
import { ActionNotice, useDialogs } from "../dialogs";
import { DataTable, DataRow, EmptyHint, ErrorState, LoadingState, RowAction } from "../components";
import type { SubscriptionRow } from "../types";

const statusTone: Record<string, "purple" | "blue" | "coral" | "neutral"> = {
  premium: "purple",
  trial: "blue",
  expired: "coral",
  available: "neutral"
};

export function SubscriptionsPage() {
  const { t, formatDate } = useI18n();
  const dialogs = useDialogs();
  const [params] = useSearchParams();
  const appFilter = params.get("app") ?? "";
  const subs = useAdminResource<SubscriptionRow[]>("/subscriptions?limit=100");
  const rows = appFilter ? (subs.data ?? []).filter((r) => r.appSlug === appFilter) : (subs.data ?? []);
  const [notice, setNotice] = useState<{ error?: string; info?: string }>({});

  const statusText = (status: string) =>
    status === "premium"
      ? "Premium"
      : status === "trial"
        ? "Trial"
        : status === "expired"
          ? t("admin.users.expired")
          : status === "available"
            ? t("admin.subs.statusAvailable")
            : status;

  const grantPremium = async (item: SubscriptionRow) => {
    const months = await dialogs.prompt({
      title: t("admin.users.grantTitle"),
      message: t("admin.subs.grantMessage", { displayName: item.displayName, email: item.email, app: item.appSlug }),
      label: t("admin.users.fieldMonths"),
      inputType: "number",
      initial: "1",
      hint: t("admin.subs.grantHint"),
      validate: (value) => {
        const parsed = Number.parseInt(value, 10);
        if (!Number.isFinite(parsed) || String(parsed) !== value.trim() || parsed < 1 || parsed > 120) {
          return t("admin.users.grantMonthsError");
        }
        return "";
      },
      submitLabel: t("admin.users.grantTitle")
    });
    if (months === null) return;
    try {
      await adminApi(`/users/${item.userId}/premium`, { method: "POST", body: JSON.stringify({ appSlug: item.appSlug, months: Number.parseInt(months, 10) }) });
      setNotice({ info: t("admin.users.grantSuccess", { email: item.email, app: item.appSlug, months }) });
      subs.reload();
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.users.actionFailed") });
    }
  };

  const revokePremium = async (item: SubscriptionRow) => {
    const confirmed = await dialogs.confirm({
      title: t("admin.users.revokeTitle"),
      message: t("admin.users.revokeMessage", { displayName: item.displayName, email: item.email, app: item.appSlug }),
      confirmLabel: t("admin.users.revokeConfirm"),
      danger: true
    });
    if (!confirmed) return;
    try {
      await adminApi(`/users/${item.userId}/revoke`, { method: "POST", body: JSON.stringify({ appSlug: item.appSlug }) });
      setNotice({ info: t("admin.users.revokedNotice", { email: item.email, app: item.appSlug }) });
      subs.reload();
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.users.actionFailed") });
    }
  };

  const labels = [
    "App",
    t("admin.users.colUser"),
    t("auth.email"),
    t("admin.status"),
    t("admin.subs.colTrialUntil"),
    t("admin.subs.colPremiumUntil"),
    t("admin.users.colActions")
  ];
  const columns = [
    { label: labels[0], width: "minmax(120px, .9fr)" },
    { label: labels[1], width: "minmax(160px, 1.2fr)" },
    { label: labels[2], width: "minmax(200px, 1.4fr)" },
    { label: labels[3], width: "minmax(110px, .8fr)" },
    { label: labels[4], width: "minmax(120px, .9fr)" },
    { label: labels[5], width: "minmax(120px, .9fr)" },
    { label: labels[6], width: "minmax(110px, .8fr)" }
  ];

  return (
    <div className="admin-page">
      <PageHeader title={t("admin.subscriptions")} subtitle={t("admin.subs.subtitle")} />
      <ActionNotice error={notice.error} info={notice.info} onDismiss={() => setNotice({})} />

      {subs.status === "error" && <ErrorState message={subs.error} onRetry={() => void subs.reload()} />}
      {subs.status === "loading" && <LoadingState rows={4} />}
      {subs.status === "ready" && (
        rows.length === 0
          ? <EmptyHint>{t("admin.subs.empty")}</EmptyHint>
          : (
            <>
              <DataTable columns={columns} minWidth={900}>
                {rows.map((item) => (
                  <DataRow key={`${item.userId}-${item.appSlug}`} labels={labels}>
                    <span className="admin-app-cell"><span className="admin-app-init" aria-hidden="true">{item.appSlug.charAt(0).toUpperCase()}</span><strong>{item.appSlug.charAt(0).toUpperCase() + item.appSlug.slice(1)}</strong></span>
                    <span className="admin-person">
                      <i aria-hidden="true">{(item.displayName || "?").slice(0, 1).toUpperCase()}</i>
                      <span className="admin-person__meta"><strong>{item.displayName}</strong></span>
                    </span>
                    <span className="admin-wrap">{item.email}</span>
                    <Badge tone={statusTone[item.status] ?? "neutral"}>{statusText(item.status)}</Badge>
                    <span className="admin-time">{item.trialEndsAt ? formatDate(item.trialEndsAt) : "—"}</span>
                    <span className="admin-time">{item.premiumUntil ? formatDate(item.premiumUntil) : "—"}</span>
                    <span className="admin-row-actions">
                      <RowAction label={t("admin.subs.grantAction", { app: item.appSlug })} icon="plus" onClick={() => void grantPremium(item)} />
                      <RowAction
                        label={t("admin.subs.revokeAction")}
                        icon="trash"
                        danger
                        disabled={item.status !== "premium"}
                        onClick={() => void revokePremium(item)}
                      />
                    </span>
                  </DataRow>
                ))}
              </DataTable>
              {rows.length >= 100 && <p className="admin-limit-note">{t("admin.users.limitNote")}</p>}
            </>
          )
      )}
    </div>
  );
}
